import type { TextAreaRef } from "antd/lib/input/TextArea";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { BTTextArea } from "commonComponents/btWrappers/BTTextArea/BTTextArea";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import "./EditableTitle.less";

export interface IEditableTitleProps {
    id: string;
    value: string;
    readOnly?: boolean;
    onChange: (value: string) => void;
    onCancel: () => void;
    onBlur?: (value: string) => void;
    maxLength?: number;
    "data-testid": string;
    className?: string;
    placeholder?: string;
    appendContent?: ReactNode;
    setEditListener?: (id: string, callback: () => void) => void;
    useTextTruncate?: boolean;
    maxRows?: number;
}

export const EditableTitle = ({
    id,
    value,
    readOnly = false,
    onChange,
    onCancel,
    onBlur,
    maxLength,
    "data-testid": testId,
    className,
    placeholder = "",
    appendContent,
    setEditListener,
    useTextTruncate = false,
    maxRows = 12,
}: IEditableTitleProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [capturedHeight, setCapturedHeight] = useState<number | null>(null);
    const textareaRef = useRef<TextAreaRef>(null);
    const viewModeRef = useRef<HTMLDivElement>(null);

    const handleViewClick = useCallback(() => {
        if (!readOnly && viewModeRef.current) {
            const height = viewModeRef.current.offsetHeight;
            setCapturedHeight(height);
            setIsEditing(true);
        }
    }, [readOnly]);

    const onSetIsEditing = useCallback(() => {
        handleViewClick();
    }, [handleViewClick]);

    useEffect(() => {
        setEditListener?.(id, onSetIsEditing);
    }, [id, setEditListener, onSetIsEditing]);

    useEffect(() => {
        if (isEditing && textareaRef.current?.resizableTextArea?.textArea) {
            textareaRef.current.resizableTextArea.textArea.focus();
            textareaRef.current.resizableTextArea.textArea.select();
        }
    }, [isEditing]);

    const handleBlur = useCallback(
        (_id: string | number, _touched: boolean, newValue: string | undefined) => {
            setIsEditing(false);
            setCapturedHeight(null);

            const finalValue = (newValue ?? "").slice(0, maxLength);
            onBlur?.(finalValue);
        },
        [onBlur, maxLength],
    );

    const handleChange = useCallback(
        (_id: string | number, newValue: string) => {
            onChange(newValue);
        },
        [onChange],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                textareaRef.current?.resizableTextArea?.textArea?.blur();
            } else if (e.key === "Escape") {
                e.preventDefault();
                onCancel();
                setIsEditing(false);
            }
        },
        [onCancel],
    );

    if (isEditing) {
        return (
            <>
                <div className="EditableTitleWrapper">
                    <BTTextArea
                        id={id}
                        data-testid={testId}
                        value={value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        maxLength={maxLength ?? undefined}
                        fieldRef={textareaRef}
                        debouncingContext={{ useDebouncing: false }}
                        blurOnChange={false}
                        className={cn("EditableTitle", "EditableTitleEdit", className)}
                        charsRemainingClassName="EditableTitleCharsRemaining"
                        style={{
                            height: capturedHeight ? `${capturedHeight}px` : undefined,
                            lineHeight: 1.35,
                        }}
                        autoSize={{ minRows: 1, maxRows: maxRows }}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                {appendContent && <span className="EditableTitleAppend">{appendContent}</span>}
            </>
        );
    }

    return (
        <>
            <div
                ref={viewModeRef}
                className={cn("EditableTitle", "EditableTitleView", className, {
                    EditableTitleReadonly: readOnly,
                    EditableTitleClickable: !readOnly,
                    EditableTitlePlaceholder: !value,
                })}
                onClick={handleViewClick}
                data-testid={testId}
                role={readOnly ? undefined : "button"}
                tabIndex={readOnly ? undefined : 0}
                onKeyDown={
                    readOnly
                        ? undefined
                        : (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleViewClick();
                              }
                          }
                }
            >
                {useTextTruncate ? (
                    <TextTruncate data-testid={`planPageTitle-Editable`}>
                        {value || placeholder}
                    </TextTruncate>
                ) : (
                    value || placeholder
                )}
            </div>
            {appendContent && <span className="EditableTitleAppend">{appendContent}</span>}
        </>
    );
};
